import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { Merchant, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  Group,
  MarketShell,
  type BadgeKind,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Merchants` — CityCab Market's front door, over MER-01's listing and nothing else.
 *
 * ## What it draws, and what it cannot
 *
 * | Drawn                                        | From                                              |
 * | -------------------------------------------- | ------------------------------------------------- |
 * | `Kilimani Fresh` / `Harbour Pharmacy` / …    | MER-01's `merchants.tradingName`                  |
 * | the standing beside each name                 | MER-01's `merchants.status`, and see below         |
 * | `Open now` / `Opening later`                  | **nothing** — no opening hours are published      |
 * | `Grocery · 20–30 min · KES 99 delivery`       | **nothing reachable** — see the three notes below |
 * | `4.8`                                         | **nothing** — no merchant rating is published     |
 * | `Deliver to Kilimani`                         | **nothing** — CUS-02 has a POST and no GET        |
 *
 * Three of those are the measurement's three for this screen. The other three are the same kind of
 * absence found while building it, and each is recorded:
 *
 *   - **The two group headings are a partition by opening hours.** `Open now` and `Opening later`
 *     cannot be computed from anything MER-01 or MER-02 publishes, so the rows are drawn in one
 *     group and the heading carries the absence rather than being dropped. A heading invented from
 *     `status` would have read as trading hours and been wrong twice a day.
 *   - **`20–30 min` is published and unreachable.** `merchantLocation.preparationMinutes` exists on
 *     MER-02, and MER-02 publishes **no listing of a merchant's branches** — a create, a read by
 *     `locationId`, a patch and a status command. So a screen that starts from the merchant listing
 *     cannot get to a branch, and the preparation time, the category and the delivery fee are one
 *     absence in the sub-line rather than three. `Products · stock` takes its branch from the
 *     address for the same reason.
 *   - **The badge's words are MER-01's and not the design's.** The design writes `open`,
 *     `busy, longer prep` and `opens 14:00`, which are *trading* states. MER-01 publishes a
 *     *lifecycle*: `pending_review | active | suspended | closed`. Those are different facts about
 *     the same shop, and a `suspended` merchant drawn as `open` would send a rider to a closed door.
 *     So the published word is drawn with the kind it maps to, which is `Q-80`'s rule applied to a
 *     badge instead of a glyph: derive from the one enumerated field, never from prose.
 *
 * The lead glyph is the fourth of the same kind and is the one that needed a choice rather than an
 * absence: a 42dp tile with nothing in it is an element removed. MER-01 publishes no category, type
 * or icon field at all, so every row takes `storefront` — the design's own glyph for a merchant, and
 * the one it gives the first row. A tenant of four merchants therefore draws four identical glyphs
 * where the design draws four different ones, which is visible on every capture and is reported as a
 * measured number.
 */

/**
 * A merchant row leads nowhere, and that is the fourth absence rather than an omission.
 *
 * `Products · stock` needs a catalogue, a category and a branch. COM-01 publishes a catalogue read
 * by `catalogId` and **no listing**; MER-02 publishes a branch read by `locationId` and **no
 * listing**. So nothing gets from `merchants.id` to either, and a row that navigated would have to
 * guess both. The design draws no control on these rows, so there is no control to disable — the
 * screen lists what MER-01 publishes and `Products · stock` is reached by an address that names its
 * four identifiers, the same way the seven journey screens are reached by one that names a ride.
 */
export interface MerchantsProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
}

/** The design's own glyph for a merchant, used for every row because nothing publishes one. */
const MERCHANT_GLYPH = "storefront";

/**
 * MER-01's lifecycle against the design's badge kinds.
 *
 * Total over the enumeration rather than defaulted, so a value added to MER-01's contract fails the
 * build here instead of being drawn as whatever the fallback was.
 */
const KIND_FOR_STATUS: Readonly<Record<Merchant["status"], BadgeKind>> = {
  active: "done",
  pending_review: "idle",
  suspended: "caution",
  closed: "closed",
};

/** The same, as the word in the pill. MER-01's own value, with its underscore read aloud. */
function badgeFor(merchant: Merchant): BadgeValue {
  return {
    kind: KIND_FOR_STATUS[merchant.status],
    label: merchant.status.replaceAll("_", " "),
  };
}

export function Merchants(props: MerchantsProps): ReactNode {
  const { api } = props;

  const [merchants, setMerchants] = useState<readonly Merchant[] | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });
    api
      .merchants()
      .then((answer) => {
        setMerchants(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.merchants])));
  }, [api]);

  useEffect(load, [load]);

  if (state.kind !== "ready" || merchants === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        purpose="Finding the merchants near you."
        barTitle="Market"
        barSub="CityCab"
      />
    );
  }

  const rows: RowValue[] = merchants.map((merchant) => ({
    id: merchant.id,
    lead: MERCHANT_GLYPH,
    title: merchant.tradingName,
    subAbsent: "No category, time or fee",
    amountAbsent: "No rating",
    badge: badgeFor(merchant),
    dimmed: merchant.status === "closed",
  }));

  return (
    <MarketShell
      screen="merchants"
      /*
       * `menu`, which is the design's own left control on this screen and on no other.
       *
       * It has no destination. `10 Rider App.dc.html` draws the same glyph on `1 Where to` and draws
       * no drawer anywhere, which is recorded as Q-84; this file does the same thing on this screen.
       * So the control is drawn, because the design draws it, and pressing it says there is nothing
       * behind it rather than opening a drawer this build would have had to design.
       */
      left={{
        glyph: "menu",
        label: "Menu",
        unavailable: "No design file draws what is behind this control (Q-84)",
      }}
      barTitle="Market"
      barSub={deliverTo()}
      right={{
        glyph: "search",
        label: "Search merchants",
        unavailable:
          "GET /v1/merchants (MER-01) takes status, limit and after — there is no text filter to search on",
      }}
      badge={{ kind: "done", label: `${String(merchants.length)} merchants` }}
      title="What do you need today?"
      sub="Groceries, pharmacy and food from local merchants."
    >
      <Group headingAbsent="Opening hours not published" rows={rows} empty={<EmptyMerchants />} />
    </MarketShell>
  );
}

/**
 * `Deliver to Kilimani` — the saved place a rider is shopping for.
 *
 * `POST /v1/customers/{customerId}/saved-places` exists and there is **no GET**, which is the same
 * gap `1 Where to` draws two absences for and `Checkout` is blocked partly on. A rider can record a
 * place and no screen can read one back.
 */
function deliverTo(): ReactNode {
  return <Absent words="No saved place: CUS-02 publishes a POST and no GET" />;
}

/**
 * The empty state, which on this screen is a real answer rather than a failure.
 *
 * A tenant with no merchants is a tenant that has not onboarded any, and MER-01 answering an empty
 * page says exactly that. It is drawn inside the group's own card, so the card is never a 1px line.
 * The words are this build's; the design draws no empty state for this screen.
 */
function EmptyMerchants(): ReactNode {
  return (
    <div className="row">
      <span className="row__middle">
        <span className="row__title">No merchants yet</span>
        <span className="row__sub">This operator has not opened any shops in the app.</span>
      </span>
    </div>
  );
}
